import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookingModal } from '../components/shared/BookingModal';
import { Target, Users, Award, Zap, ArrowRight, CheckCircle } from 'lucide-react';

const values = [
    {
        icon: Target,
        title: 'Revenue-First',
        description: 'We measure success by the leads and sales we generate, not vanity metrics.',
    },
    {
        icon: Zap,
        title: 'Future-Ready',
        description: 'Always ahead of algorithm changes and emerging search platforms.',
    },
    {
        icon: Users,
        title: 'Partnership',
        description: 'We become an extension of your team, not just another vendor.',
    },
    {
        icon: Award,
        title: 'Excellence',
        description: 'Every strategy is crafted with meticulous attention to detail.',
    },
];

const team = [
    { name: 'Vikram Singh', role: 'Founder & CEO', expertise: 'SEO Strategy' },
    { name: 'Priya Patel', role: 'Head of Operations', expertise: 'Client Success' },
    { name: 'Arjun Reddy', role: 'Technical SEO Lead', expertise: 'Technical Audits' },
    { name: 'Sneha Kapoor', role: 'Content Director', expertise: 'Content Strategy' },
];

const milestones = [
    { year: '2020', event: 'Founded with a vision to democratize SEO' },
    { year: '2021', event: 'Served 50+ clients, launched GMB services' },
    { year: '2022', event: '100+ clients, expanded to programmatic SEO' },
    { year: '2023', event: 'Pioneered AI Search Optimization services' },
    { year: '2024', event: '300+ clients, launched industry-specific solutions' },
];

export default function AboutPage() {
    const [bookingOpen, setBookingOpen] = useState(false);

    return (
        <div className="relative" data-testid="about-page">
            {/* Hero */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="max-w-4xl">
                        <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                            About Aitrixy
                        </p>
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                            We Build Search Empires
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                            Aitrixy is a next-generation search visibility agency. We help businesses 
                            dominate every platform where customers search — from Google and Maps to 
                            AI-powered search engines like ChatGPT and Perplexity.
                        </p>
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                                Our Mission
                            </h2>
                            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                                To make every business visible where it matters most. We believe that 
                                great products deserve to be found, and we're committed to making that 
                                happen through strategic, ethical, and results-driven SEO.
                            </p>
                            <p className="text-lg text-muted-foreground leading-relaxed">
                                Unlike traditional agencies that chase traffic, we focus on revenue. 
                                Every strategy we implement is designed to bring you customers, not 
                                just visitors.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { value: '300+', label: 'Happy Clients' },
                                { value: '10M+', label: 'Keywords Ranked' },
                                { value: '98%', label: 'Client Retention' },
                                { value: '4+', label: 'Years Experience' },
                            ].map((stat, index) => (
                                <div 
                                    key={index}
                                    className="p-6 rounded-2xl border border-border bg-background text-center"
                                >
                                    <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                                        {stat.value}
                                    </div>
                                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                            Our Values
                        </h2>
                        <p className="text-lg text-muted-foreground">
                            The principles that guide everything we do.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map((value, index) => (
                            <div 
                                key={index}
                                className="p-8 rounded-2xl border border-border bg-background"
                            >
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                                    <value.icon className="w-6 h-6 text-primary" />
                                </div>
                                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                                <p className="text-muted-foreground">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-12 text-center">
                            Our Journey
                        </h2>

                        <div className="relative">
                            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-1/2" />
                            
                            {milestones.map((milestone, index) => (
                                <div 
                                    key={index}
                                    className={`relative flex items-center gap-8 mb-8 ${
                                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                                    }`}
                                >
                                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : ''} pl-12 md:pl-0`}>
                                        <div className="text-2xl font-bold text-primary mb-2">
                                            {milestone.year}
                                        </div>
                                        <p className="text-muted-foreground">{milestone.event}</p>
                                    </div>
                                    <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                                        <CheckCircle className="w-4 h-4 text-primary-foreground" />
                                    </div>
                                    <div className="flex-1 hidden md:block" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Team */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                            Meet the Team
                        </h2>
                        <p className="text-lg text-muted-foreground">
                            Passionate experts dedicated to your growth.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {team.map((member, index) => (
                            <div 
                                key={index}
                                className="p-6 rounded-2xl border border-border bg-background text-center"
                            >
                                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-primary">
                                    {member.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <h3 className="font-semibold mb-1">{member.name}</h3>
                                <p className="text-sm text-primary mb-2">{member.role}</p>
                                <p className="text-xs text-muted-foreground">{member.expertise}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                            Ready to Work Together?
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8">
                            Let's discuss how we can help your business dominate search.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Button 
                                size="lg"
                                className="btn-glow rounded-full px-8"
                                onClick={() => setBookingOpen(true)}
                                data-testid="about-cta-btn"
                            >
                                Book Strategy Call
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                            <Button 
                                variant="outline"
                                size="lg"
                                asChild
                                className="rounded-full px-8"
                            >
                                <Link to="/contact">Contact Us</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
        </div>
    );
}
